import { c as _c } from "react/compiler-runtime";
import { Background, BackgroundVariant, Controls, type EdgeTypes, type Node, type NodeTypes, ReactFlow, SelectionMode, useReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { MonacoProvider, useEscOperationStack, useShortcut } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useEffect } from 'react';
import { GraphUndoRedo } from '@/features/playbook-edit';
import { ActionNode } from './components/action-node';
import AiDecisionNode from './components/ai-decision-node';
import AiResearchNode from './components/ai-research-node';
import AuthenticationNode from './components/authentication-node';
import BankLinkNode from './components/bank-link-node';
import BoKycNode from './components/bo-kyc-node';
import BranchNode from './components/branch-node';
import BusinessDataNode from './components/business-data-node';
import CodeExecNode from './components/code-exec-node';
import CollectCardNode from './components/collect-card-node';
import CollectDataNode from './components/collect-data-node';
import CollectDocumentNode from './components/collect-document-node';
import CompleteNode from './components/complete-node';
import ConditionalNode from './components/conditional-node';
import { CustomDataNode } from './components/custom-data-node';
import { CustomDocumentNode } from './components/custom-document';
import DataAssignmentNode from './components/data-assignment-node/data-assignment-node';
import DocumentExtractionNode from './components/document-extraction-node';
import Edge from './components/edge';
import { EdgeLabelNode } from './components/edge-label-node';
import ErrorWarningList from './components/error-warning-list';
import ExpressionNode from './components/expression-node';
import ExternalApiNode from './components/external-api-node';
import IdentityDataNode from './components/identity-data-node';
import { IdentityDocumentNode } from './components/identity-document';
import InputSchemaNode from './components/input-schema-node';
import InvestorProfileNode from './components/investor-profile-node';
import NodePanel from './components/node-panel/node-panel';
import { NodePanelProvider } from './components/node-panel/node-panel-context';
import OutputNode from './components/output-node';
import PlaceholderNode from './components/placeholder-node';
import PrerequisiteNode from './components/prerequisite-node';
import { RulesNode } from './components/rules-node';
import { SelfieNode } from './components/selfie-node';
import { TaktileNode } from './components/taktile-node';
import VendorIntegrationNode from './components/vendor-integration-node';
import VerificationCheckNode from './components/verification-check-node';
import { VerifiedPrefillNode } from './components/verified-prefill-node';
import WatchlistReviewNode from './components/watchlist-review-node';
import { initialDirtyState, useDirtyStore } from './hooks/use-dirty';
import { useGetStatuses } from './hooks/use-get-statuses';
import useLayout from './hooks/use-layout';
import useMultiSelect from './hooks/use-multi-select';
import useSidebar from './hooks/use-sidebar';
import useStore from './hooks/use-store';
import { defaultEdgeOptions, HIGHLIGHTED_EDGE_CLASS_NAME, HIGHLIGHTED_EDGE_Z_INDEX } from './playbook-builder.constants';
import { type NodeTypesKey, reactFlowNodeType } from './playbook-builder.types';
const nodeComponents: Record<NodeTypesKey, NodeTypes[string]> = {
  action: ActionNode,
  ai_research: AiResearchNode,
  ai_decision: AiDecisionNode,
  authentication: AuthenticationNode,
  collect_data: CollectDataNode,
  collect_custom_data: CustomDataNode,
  collect_document: CollectDocumentNode,
  identity_document: IdentityDocumentNode,
  selfie: SelfieNode,
  custom_document: CustomDocumentNode,
  conditional: ConditionalNode,
  rules: RulesNode,
  verification_check: VerificationCheckNode,
  verified_prefill: VerifiedPrefillNode,
  bo_kyc: BoKycNode,
  investor_profile: InvestorProfileNode,
  taktile: TaktileNode,
  input_schema: InputSchemaNode,
  prerequisite: PrerequisiteNode,
  output: OutputNode,
  complete: CompleteNode,
  identity_data: IdentityDataNode,
  business_data: BusinessDataNode,
  external_api: ExternalApiNode,
  vendor_integration: VendorIntegrationNode,
  placeholder: PlaceholderNode,
  join_point: PlaceholderNode,
  branch: BranchNode,
  collect_card_data: CollectCardNode,
  bank_link: BankLinkNode,
  edge_label: EdgeLabelNode,
  assign_data: DataAssignmentNode,
  common_expression: ExpressionNode,
  code_exec: CodeExecNode,
  document_extraction: DocumentExtractionNode,
  watchlist_review: WatchlistReviewNode
};
const nodeTypes: NodeTypes = Object.fromEntries(Object.entries(nodeComponents).map(([kind, component]) => [reactFlowNodeType(kind as NodeTypesKey), component]));
const edgeTypes: EdgeTypes = {
  edge: Edge
};
type PlaybookBuilderProps = {
  bannerComponent?: React.ReactNode;
  headerComponent?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
};
const PlaybookBuilder = t0 => {
  const $ = _c(55);
  const {
    bannerComponent,
    headerComponent,
    className,
    children
  } = t0;
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    isInitialized
  } = useLayout();
  const reactFlow = useReactFlow();
  const {
    data: t1,
    visibleBanners,
    executionTraceBanners
  } = useStore();
  const {
    canEdit
  } = t1;
  let t2;
  if ($[0] !== executionTraceBanners || $[1] !== visibleBanners) {
    t2 = [...visibleBanners, ...executionTraceBanners];
    $[0] = executionTraceBanners;
    $[1] = visibleBanners;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const errorItems = t2;
  useGetStatuses();
  const {
    isShiftPressed,
    onSelectionChange
  } = useMultiSelect();
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useEffect(_temp2, t3);
  let t4;
  if ($[4] !== reactFlow) {
    t4 = (_, node) => {
      if (!node.selectable) {
        return;
      }
      reactFlow.setNodes(nodes_0 => nodes_0.map(n => n.id === node.id ? {
        ...n,
        selected: false
      } : n));
    };
    $[4] = reactFlow;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const undoNodeSelection = t4;
  const {
    onEscKeyDown
  } = useEscOperationStack();
  let t5;
  if ($[6] !== onEscKeyDown) {
    t5 = {
      onEscKeyDown
    };
    $[6] = onEscKeyDown;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  useShortcut(t5);
  const {
    isAnyOpen
  } = useSidebar();
  let t6;
  if ($[8] !== isAnyOpen || $[9] !== onEscKeyDown) {
    t6 = e => {
      if (isAnyOpen && e.target instanceof HTMLElement && e.target.classList.contains("draggable")) {
        onEscKeyDown();
      }
    };
    $[8] = isAnyOpen;
    $[9] = onEscKeyDown;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleGraphClick = t6;
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <style>{`
        .playbook-builder svg:has(path.${HIGHLIGHTED_EDGE_CLASS_NAME}) {
          z-index: ${HIGHLIGHTED_EDGE_Z_INDEX} !important;
        }
        .playbook-builder .react-flow__panel.bottom {
          bottom: 40px;
        }
        .playbook-builder .react-flow__selection {
          background: rgba(59, 130, 246, 0.06);
          border: 1px dashed rgba(59, 130, 246, 0.5);
          border-radius: 4px;
        }
        .playbook-builder .react-flow__node.selected > div {
          outline: 2px solid rgba(59, 130, 246, 0.6);
          outline-offset: 2px;
          border-radius: 10px;
        }
      `}</style>;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== className) {
    t8 = cx("flex relative flex-col flex-1 min-h-0 overflow-hidden playbook-builder", className);
    $[12] = className;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = !isInitialized;
  const t10 = !!children;
  let t11;
  if ($[14] !== t10) {
    t11 = cx("relative flex flex-1 min-h-0 bg-canvas", {
      hidden: t10
    });
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      x: 0,
      y: 0,
      zoom: 1.25
    };
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const t13 = !isInitialized;
  const t14 = !isShiftPressed;
  let t15;
  if ($[17] !== isShiftPressed || $[18] !== t13 || $[19] !== t14) {
    t15 = cx("flex-1 transition-opacity duration-200", {
      "opacity-0": t13,
      "cursor-crosshair": isShiftPressed,
      "cursor-grab": t14
    });
    $[17] = isShiftPressed;
    $[18] = t13;
    $[19] = t14;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const t16 = !isShiftPressed;
  let t17;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      hideAttribution: true
    };
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  let t19;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Background color="var(--playbook-canvas-dot)" variant={BackgroundVariant.Dots} size={2} gap={20} />;
    t19 = <Controls showInteractive={false} className="!shadow-sm rounded-default overflow-hidden [&>button]:!bg-primary [&>button]:!text-primary [&>button]:!border-tertiary [&>button]:hover:!bg-hover [&>button]:hover:!text-tertiary fixed !bottom-3 max-md:bottom-[max(0.75rem,env(safe-area-inset-bottom))]!" />;
    $[22] = t18;
    $[23] = t19;
  } else {
    t18 = $[22];
    t19 = $[23];
  }
  let t20;
  if ($[24] !== children || $[25] !== errorItems) {
    t20 = children ? null : <ErrorWarningList items={errorItems} />;
    $[24] = children;
    $[25] = errorItems;
    $[26] = t20;
  } else {
    t20 = $[26];
  }
  let t21;
  if ($[27] !== canEdit) {
    t21 = canEdit && <GraphUndoRedo />;
    $[27] = canEdit;
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  let t22;
  if ($[29] !== edges || $[30] !== handleGraphClick || $[31] !== isShiftPressed || $[32] !== nodes || $[33] !== onEdgesChange || $[34] !== onNodesChange || $[35] !== onSelectionChange || $[36] !== t15 || $[37] !== t16 || $[38] !== t20 || $[39] !== t21 || $[40] !== undoNodeSelection) {
    t22 = <ReactFlow defaultEdgeOptions={defaultEdgeOptions} defaultViewport={t12} edges={edges} edgeTypes={edgeTypes} nodes={nodes} nodesDraggable={false} nodeTypes={nodeTypes} onEdgesChange={onEdgesChange} onClick={handleGraphClick} className={t15} onNodesChange={onNodesChange} onSelectionChange={onSelectionChange} selectionOnDrag={isShiftPressed} selectionMode={SelectionMode.Partial} panOnDrag={t16} onNodeClick={undoNodeSelection} proOptions={t17} deleteKeyCode={null} maxZoom={1} minZoom={0.3}>{t18}{t19}{t20}{t21}</ReactFlow>;
    $[29] = edges;
    $[30] = handleGraphClick;
    $[31] = isShiftPressed;
    $[32] = nodes;
    $[33] = onEdgesChange;
    $[34] = onNodesChange;
    $[35] = onSelectionChange;
    $[36] = t15;
    $[37] = t16;
    $[38] = t20;
    $[39] = t21;
    $[40] = undoNodeSelection;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <NodePanel />;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== t22) {
    t24 = <MonacoProvider languageName="template">{t22}{t23}</MonacoProvider>;
    $[43] = t22;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== t11 || $[46] !== t24 || $[47] !== t9) {
    t25 = <div data-percy-context-container={true} aria-busy={t9} className={t11}>{t24}</div>;
    $[45] = t11;
    $[46] = t24;
    $[47] = t9;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== bannerComponent || $[50] !== children || $[51] !== headerComponent || $[52] !== t25 || $[53] !== t8) {
    t26 = <>{t7}<NodePanelProvider><div className={t8}>{bannerComponent}{headerComponent}{children}{t25}</div></NodePanelProvider></>;
    $[49] = bannerComponent;
    $[50] = children;
    $[51] = headerComponent;
    $[52] = t25;
    $[53] = t8;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  return t26;
};
export default PlaybookBuilder;
function _temp() {
  return useDirtyStore.getState().set(initialDirtyState);
}
function _temp2() {
  return _temp;
}